import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { clamp } from 'lodash-es';
import { PlumaSideNav as PlumaSideNavCss } from '@customerio/pluma-components/css/components';
import { useSetSideNavState, useToggleSideNav } from '@customerio/pluma-components/react';
import { GlobalCSSVariable, useThemeContext } from '@/context/theme';
import { withNavCollapseInteraction } from './nav-collapse-interaction';

export const NAV_RAIL_WIDTH = Number.parseInt(PlumaSideNavCss.values.collapsedWidth);
export const NAV_EXPANDED_WIDTH = Number.parseInt(PlumaSideNavCss.values.expandedWidth);

const SNAP_AT = Math.round((NAV_RAIL_WIDTH + NAV_EXPANDED_WIDTH) / 2);

const CLICK_SLOP_PX = 3;

type DragGesture = {
  startX: number;
  startWidth: number;
  wasCollapsed: boolean;
  moved: boolean;
  liveWidth: number;
  // Compared instead of the render-scoped `isCollapsed`, which goes stale
  // between a crossing and its committed re-render and would re-fire
  // setIsCollapsed (and the provider's onCollapseChange) per move.
  previewIsCollapsed: boolean;
};

type UseNavResizeOptions = {
  isCollapsed: boolean;
  isEnabled: boolean;
};

/**
 * Drag the nav's trailing edge; it follows the pointer but only ever settles
 * on the rail or the expanded width. Sole writer of `--hydra-main-nav-width`:
 * a second writer keyed on the collapsed state yanks the width back to a
 * snapped value the moment a drag crosses the snap point.
 */
export function useNavResize({ isCollapsed, isEnabled }: UseNavResizeOptions) {
  const { setVariable, removeVariable } = useThemeContext();
  const setIsCollapsed = useSetSideNavState();
  const toggleSideNav = useToggleSideNav();

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragGesture | null>(null);

  // Mirrors SideNav's own `effectiveIsCollapsed`: settings pages share the
  // app's SideNavProvider, so state can say "collapsed" while the on-screen
  // nav renders expanded — the rail width there would clip its labels.
  const restingIsCollapsed = isEnabled && isCollapsed;

  // Written straight to the document element so a drag repaints without going
  // through React; the nav, the affix and the dev helper all read it.
  const publishWidth = useCallback(
    (width: number) => {
      setVariable(GlobalCSSVariable.MainNavWidth, `${width}px`);
    },
    [setVariable],
  );

  // Layout effect so the variable exists before first paint: the nav has CSS
  // fallbacks, but the affix and the dev helper read it with their own guesses.
  useLayoutEffect(() => {
    if (isDragging) return;
    publishWidth(restingIsCollapsed ? NAV_RAIL_WIDTH : NAV_EXPANDED_WIDTH);
  }, [isDragging, publishWidth, restingIsCollapsed]);

  const releaseDrag = useCallback(() => {
    dragRef.current = null;
    setIsDragging(false);
    // Restore the stylesheet's duration so the snap eases instead of jumping.
    removeVariable(GlobalCSSVariable.MainNavTransitionTime);
  }, [removeVariable]);

  // A route change to a settings page unmounts the edge mid-gesture while this
  // hook stays mounted, so no pointerup will ever release what the drag holds.
  // End it here: revert the preview and let the resting effect take back over.
  useEffect(() => {
    if (isEnabled) {
      return;
    }
    const drag = dragRef.current;
    if (drag == null) {
      return;
    }
    releaseDrag();
    withNavCollapseInteraction('drag-preview', () => setIsCollapsed?.(drag.wasCollapsed));
  }, [isEnabled, releaseDrag, setIsCollapsed]);

  // A drag cut short by unmount would otherwise leave the 0ms override in
  // place for the rest of the session. `removeVariable` is module-scoped in
  // theme.tsx, so this cleanup runs only on unmount.
  useEffect(() => {
    return () => {
      if (dragRef.current != null) {
        removeVariable(GlobalCSSVariable.MainNavTransitionTime);
      }
    };
  }, [removeVariable]);

  const handlePointerDown = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      // A right- or middle-click would capture the pointer and its
      // movement-free pointerup would fall through to the toggle.
      if (!isEnabled || event.button !== 0 || !event.isPrimary) return;
      event.preventDefault();
      event.currentTarget.setPointerCapture(event.pointerId);

      const width = isCollapsed ? NAV_RAIL_WIDTH : NAV_EXPANDED_WIDTH;
      dragRef.current = {
        startX: event.clientX,
        startWidth: width,
        wasCollapsed: isCollapsed,
        moved: false,
        liveWidth: width,
        previewIsCollapsed: isCollapsed,
      };
      setIsDragging(true);
      // Transitions off while the pointer owns the width, or the nav lags it.
      setVariable(GlobalCSSVariable.MainNavTransitionTime, '0ms');
    },
    [isCollapsed, isEnabled, setVariable],
  );

  const handlePointerMove = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (drag == null) return;

      const delta = event.clientX - drag.startX;
      if (Math.abs(delta) > CLICK_SLOP_PX) {
        drag.moved = true;
      }

      // Pointer capture keeps events flowing while the cursor sits outside the
      // clamp range, so most frames at the tail of a flick change nothing.
      const width = clamp(drag.startWidth + delta, NAV_RAIL_WIDTH, NAV_EXPANDED_WIDTH);
      if (width === drag.liveWidth) return;
      drag.liveWidth = width;
      publishWidth(width);

      // Swap the nav's contents as the boundary is crossed — a preview, not a
      // decision, so the analytics reporter must not count it.
      const shouldCollapse = width < SNAP_AT;
      if (shouldCollapse !== drag.previewIsCollapsed) {
        drag.previewIsCollapsed = shouldCollapse;
        withNavCollapseInteraction('drag-preview', () => setIsCollapsed?.(shouldCollapse));
      }
    },
    [publishWidth, setIsCollapsed],
  );

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const drag = dragRef.current;
      if (drag == null) return;

      if (event.currentTarget.hasPointerCapture(event.pointerId)) {
        event.currentTarget.releasePointerCapture(event.pointerId);
      }
      // The resting-width effect re-runs off `isDragging` and publishes the
      // snapped value, so there is nothing to write here.
      releaseDrag();

      if (!drag.moved) {
        toggleSideNav?.();
        return;
      }
      // Pluma fires onCollapseChange even for an unchanged value, so a drag
      // that ends where it began stays an untracked preview.
      const shouldCollapse = drag.liveWidth < SNAP_AT;
      withNavCollapseInteraction(shouldCollapse === drag.wasCollapsed ? 'drag-preview' : 'drag', () =>
        setIsCollapsed?.(shouldCollapse),
      );
    },
    [releaseDrag, setIsCollapsed, toggleSideNav],
  );

  return { isDragging, handlePointerDown, handlePointerMove, endDrag };
}
